import {
  recoveryCompleteResponseSchema,
  recoveryCsrfResponseSchema,
  recoveryOkResponseSchema,
  type RecoveryCompleteResponse,
  type RecoveryOkResponse,
} from '@repo/auth-contracts';
import type { ZodType } from 'zod';
import { InteractionApiError, REQUEST_TIMEOUT_MS } from './interaction-api';

export interface RecoveryApi {
  /** Mints the double-submit pair; recovery pages have no interaction to read one from. */
  csrfToken(): Promise<{ csrfToken: string; resetTtlMinutes: number }>;
  start(
    email: string,
    csrfToken: string,
    /** The active screen language — the reset mail follows the screen. */
    locale?: 'en' | 'de',
  ): Promise<RecoveryOkResponse>;
  /**
   * Resolves to `{status:'ok'}` when the password was set, or
   * `{status:'invalid_token'}` when the link was unusable — so the screen shows
   * "request a new link" instead of a false success (password-reset.md AC6).
   */
  complete(
    token: string,
    newPassword: string,
    csrfToken: string,
  ): Promise<RecoveryCompleteResponse>;
}

async function parseOrThrow<T>(
  res: Response,
  schema: ZodType<T>,
  /**
   * What a 400 means on THIS endpoint. Both recovery routes answer `ok` for
   * every outcome that could be an oracle, so a 400 only ever means the input
   * itself was malformed — and which input differs per route.
   */
  badRequestCode: string,
): Promise<T> {
  const body: unknown = await res.json().catch(() => null);

  if (!res.ok) {
    if (res.status === 429) throw new InteractionApiError('rate_limited', 429);
    throw new InteractionApiError(
      res.status === 400 ? badRequestCode : 'unknown',
      res.status,
    );
  }

  const parsed = schema.safeParse(body);
  if (!parsed.success) throw new InteractionApiError('unknown', res.status);
  return parsed.data;
}

/**
 * Client for the standalone recovery surface. Same cross-origin rules as the
 * interaction API — credentials included so the CSRF cookie travels, and the
 * token echoed in `x-csrf-token` (ADR 0010).
 */
export function createRecoveryApi(baseUrl: string): RecoveryApi {
  const base = `${baseUrl.replace(/\/+$/, '')}/recovery`;

  async function send<T>(
    path: string,
    schema: ZodType<T>,
    badRequestCode: string,
    init?: { body?: unknown; csrfToken?: string },
  ): Promise<T> {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    let res: Response;
    try {
      res = await fetch(`${base}${path}`, {
        method: init?.body === undefined ? 'GET' : 'POST',
        credentials: 'include',
        signal: controller.signal,
        headers: {
          accept: 'application/json',
          ...(init?.body !== undefined && {
            'content-type': 'application/json',
          }),
          ...(init?.csrfToken && { 'x-csrf-token': init.csrfToken }),
        },
        ...(init?.body !== undefined && { body: JSON.stringify(init.body) }),
      });
    } catch {
      // A timeout aborts the fetch, which rejects here — the same `network`
      // outcome as any transport failure.
      throw new InteractionApiError('network', 0);
    } finally {
      clearTimeout(timeout);
    }
    return parseOrThrow(res, schema, badRequestCode);
  }

  return {
    csrfToken: () => send('/csrf', recoveryCsrfResponseSchema, 'unknown'),

    start: (email, csrfToken, locale) =>
      send('/password/start', recoveryOkResponseSchema, 'invalid_email', {
        body: { email, ...(locale ? { locale } : {}) },
        csrfToken,
      }),

    complete: (token, newPassword, csrfToken) =>
      send(
        '/password/complete',
        recoveryCompleteResponseSchema,
        // The token is opaque and the server never rejects a bad one with 400
        // (it answers `{status:'invalid_token'}` instead), so the only thing a
        // 400 can be complaining about is the password.
        'invalid_password',
        { body: { token, newPassword }, csrfToken },
      ),
  };
}
